/* -------------------------------------------------------------------------- */
/* React Query hooks for Design Schema CRUD                                  */
/* -------------------------------------------------------------------------- */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	getDesignSchema,
	getDesignSchemaOverride,
	getResolvedSchema,
	saveDesignSchema,
	saveDesignSchemaOverride,
	deleteDesignSchemaOverride,
	uploadCoverPage,
	deleteCoverPage,
	previewPdf,
	getPreviewHtml,
} from "../api/design-schema-api";
import type { PreviewHtmlResponse } from "../api/design-schema-api";
import type {
	DesignSchemaResponse,
	DesignSchemaUpdatePayload,
	ResolvedDesignSchema,
	CoverPageUploadResponse,
} from "../types/DesignSchema.type";
import { showToast } from "utils/helpers";

const SCHEMA_KEY = "design-schema";

/* ---- Queries ---- */

export const useGlobalDesignSchema = (companyId: number | undefined) => {
	return useQuery<DesignSchemaResponse>({
		queryKey: [SCHEMA_KEY, companyId, "global"],
		queryFn: () => getDesignSchema(companyId!),
		enabled: !!companyId,
	});
};

export const useDesignSchemaOverride = (
	companyId: number | undefined,
	reportType: string | undefined
) => {
	return useQuery<DesignSchemaResponse>({
		queryKey: [SCHEMA_KEY, companyId, reportType],
		queryFn: () => getDesignSchemaOverride(companyId!, reportType!),
		enabled: !!companyId && !!reportType,
	});
};

export const useResolvedSchema = (
	companyId: number | undefined,
	reportType: string | undefined
) => {
	return useQuery<ResolvedDesignSchema>({
		queryKey: [SCHEMA_KEY, companyId, reportType, "resolved"],
		queryFn: () => getResolvedSchema(companyId!, reportType!),
		enabled: !!companyId && !!reportType,
	});
};

/* ---- Mutations ---- */

export const useSaveDesignSchema = (companyId: number | undefined) => {
	const queryClient = useQueryClient();

	return useMutation<DesignSchemaResponse, Error, DesignSchemaUpdatePayload>({
		mutationFn: (payload) => saveDesignSchema(companyId!, payload),
		onSuccess: () => {
			if (companyId) {
				queryClient.invalidateQueries({ queryKey: [SCHEMA_KEY, companyId] });
			}
			showToast("Global defaults saved", "Success", true);
		},
		onError: (error) => {
			showToast(
				error.message || "Save failed -- your changes have not been saved",
				"Error"
			);
		},
	});
};

export const useSaveDesignSchemaOverride = (
	companyId: number | undefined,
	reportType: string | undefined
) => {
	const queryClient = useQueryClient();

	return useMutation<DesignSchemaResponse, Error, DesignSchemaUpdatePayload>({
		mutationFn: (payload) => saveDesignSchemaOverride(companyId!, reportType!, payload),
		onSuccess: () => {
			if (companyId) {
				queryClient.invalidateQueries({ queryKey: [SCHEMA_KEY, companyId] });
			}
			showToast(`${reportType} override saved`, "Success", true);
		},
		onError: (error) => {
			showToast(
				error.message || "Save failed -- your changes have not been saved",
				"Error"
			);
		},
	});
};

export const useDeleteOverride = (
	companyId: number | undefined,
	reportType: string | undefined
) => {
	const queryClient = useQueryClient();

	return useMutation<void, Error>({
		mutationFn: () => deleteDesignSchemaOverride(companyId!, reportType!),
		onSuccess: () => {
			if (companyId) {
				queryClient.invalidateQueries({ queryKey: [SCHEMA_KEY, companyId] });
			}
			showToast(`${reportType} override removed -- global defaults now apply.`, "Success", true);
		},
		onError: (error) => {
			showToast(
				error.message || "Failed to remove override",
				"Error"
			);
		},
	});
};

export const useUploadCoverPage = (companyId: number | undefined) => {
	const queryClient = useQueryClient();

	return useMutation<
		CoverPageUploadResponse,
		Error,
		{ file: File; reportType?: string }
	>({
		mutationFn: ({ file, reportType }) => uploadCoverPage(companyId!, file, reportType),
		onSuccess: () => {
			if (companyId) {
				queryClient.invalidateQueries({ queryKey: [SCHEMA_KEY, companyId] });
			}
		},
		onError: (error) => {
			showToast(
				error.message || "Cover page upload failed",
				"Error"
			);
		},
	});
};

export const useDeleteCoverPage = (companyId: number | undefined) => {
	const queryClient = useQueryClient();

	return useMutation<void, Error, { reportType?: string }>({
		mutationFn: ({ reportType }) => deleteCoverPage(companyId!, reportType),
		onSuccess: () => {
			if (companyId) {
				queryClient.invalidateQueries({ queryKey: [SCHEMA_KEY, companyId] });
			}
		},
		onError: (error) => {
			showToast(
				error.message || "Failed to remove cover page",
				"Error"
			);
		},
	});
};

export const usePreviewHtml = (companyId: number | undefined) => {
	return useMutation<PreviewHtmlResponse, Error, Record<string, unknown>>({
		mutationFn: (schema) => getPreviewHtml(companyId!, schema),
	});
};

export const usePreviewPdf = (companyId: number | undefined) => {
	return useMutation<{ download_url: string; filename: string }, Error, { schema: DesignSchemaUpdatePayload }>({
		mutationFn: ({ schema }) => previewPdf(companyId!, schema),
		onError: (error) => {
			showToast(
				error.message || "Preview failed -- please try again",
				"Error"
			);
		},
	});
};
